import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import RichTextDescription from '../../components/RichTextDescription';
import { findExistingCustomer, updateCustomerFromBooking } from '../../lib/customers';
import { sendSMS } from '../../lib/messaging';
import { getSiteUrl } from '../../lib/siteConfig';

interface BusinessSummary {
  id: string;
  name: string;
  slug: string;
  booking_settings?: {
    logo_url?: string;
    logo_invert?: boolean;
  };
}

interface QueCategory {
  id: string;
  category_title: string;
  services?: Array<{ 
    service: any; 
    priceOption: any; 
    secondary_services?: Array<{ service: any; priceOption: any }>;
  }>;
}

interface TaxRate {
  id: string;
  name: string;
  rate: number;
}

export default function PublicQue() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const [business, setBusiness] = useState<BusinessSummary | null>(null);
  const [isLoadingBusiness, setIsLoadingBusiness] = useState(true);
  const [categorySelectionEnabled, setCategorySelectionEnabled] = useState(true);
  const [categorySelectionLabel, setCategorySelectionLabel] = useState('Select Category');
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [categories, setCategories] = useState<QueCategory[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'category' | 'service' | 'secondary-services' | 'intake'>('category');
  const [selectedService, setSelectedService] = useState<{ service: any; priceOption: any; secondary_services?: any[] } | null>(null);
  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(null);
  const [selectedSecondaryServices, setSelectedSecondaryServices] = useState<Array<{ service: any; priceOption: any }>>([]);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [isTaxLoading, setIsTaxLoading] = useState(false);
  const [stripePayEnabled, setStripePayEnabled] = useState(false);
  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerColor, setHeaderColor] = useState('#111827');
  const [displayLogo, setDisplayLogo] = useState(false);
  const [displayCompanyName, setDisplayCompanyName] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const fetchBusiness = async () => {
      if (!businessSlug) {
        setError('Invalid business URL');
        setIsLoadingBusiness(false);
        return;
      }

      try {
        const { data, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, slug, booking_settings')
          .eq('slug', businessSlug)
          .single();

        if (businessError) throw businessError;
        setBusiness(data);
      } catch (err) {
        console.error('Error loading business:', err);
        setError('Unable to load business');
      } finally {
        setIsLoadingBusiness(false);
      }
    };

    fetchBusiness();
  }, [businessSlug]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!business?.id) return;
      setIsLoadingSettings(true);
      try {
        const { data, error: settingsError } = await supabase
          .from('que_settings')
          .select('category_selection_enabled, stripe_pay_enabled, header_enabled, header_color, display_logo, display_company_name, category_selection_label')
          .eq('business_id', business.id)
          .maybeSingle();

        if (settingsError) throw settingsError;

        if (data?.category_selection_enabled === undefined || data === null) {
          setCategorySelectionEnabled(true);
          setStripePayEnabled(false);
          setHeaderEnabled(false);
          setHeaderColor('#111827');
          setDisplayLogo(false);
          setDisplayCompanyName(false);
          setCategorySelectionLabel('Select Category');
          return;
        }

        setCategorySelectionEnabled(Boolean(data.category_selection_enabled));
        setStripePayEnabled(Boolean(data.stripe_pay_enabled));
        setHeaderEnabled(Boolean(data.header_enabled));
        setHeaderColor(data.header_color || '#111827');
        setDisplayLogo(Boolean(data.display_logo));
        setDisplayCompanyName(Boolean(data.display_company_name));
        setCategorySelectionLabel(data.category_selection_label || 'Select Category');
      } catch (err) {
        console.error('Error loading que settings:', err);
        setCategorySelectionEnabled(true);
        setStripePayEnabled(false);
        setHeaderEnabled(false);
        setHeaderColor('#111827');
        setDisplayLogo(false);
        setDisplayCompanyName(false);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [business?.id, supabase]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!business?.id || !categorySelectionEnabled) return;
      setIsCategoriesLoading(true);
      try {
        const { data, error: categoriesError } = await supabase
          .from('que_intake_flow')
          .select('id, category_title, services')
          .eq('business_id', business.id)
          .order('created_at', { ascending: true });

        if (categoriesError) throw categoriesError;
        console.log('Fetched categories from database:', JSON.stringify(data, null, 2));
        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching que categories:', err);
        setError('Unable to load queue categories');
      } finally {
        setIsCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [business?.id, categorySelectionEnabled, supabase]);

  useEffect(() => {
    const fetchTaxRates = async () => {
      if (!business?.id) return;
      setIsTaxLoading(true);
      try {
        const { data, error: taxError } = await supabase
          .from('tax_rates')
          .select('id, name, rate')
          .eq('business_id', business.id);

        if (taxError) throw taxError;
        setTaxRates(data || []);
      } catch (err) {
        console.error('Error loading tax rates:', err);
      } finally {
        setIsTaxLoading(false);
      }
    };

    fetchTaxRates();
  }, [business?.id, supabase]);

  useEffect(() => {
    if (categorySelectionEnabled) {
      if (selectedCategory) return;
      if (currentStep === 'category') return;
      setCurrentStep('category');
      return;
    }

    if (!categorySelectionEnabled && categories.length > 0 && currentStep === 'category') {
      setCurrentStep('service');
    }
  }, [categorySelectionEnabled, categories.length, selectedCategory, currentStep]);

  if (isLoadingBusiness) {
    console.log('🔴 RENDERING: Loading business...', { businessSlug });
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    console.log('🔴 RENDERING: Error state', { error });
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <p className="text-red-600 text-base font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (!business) {
    console.log('🔴 RENDERING: No business found');
    return null;
  }

  console.log('🟢 RENDERING: Main content', {
    business: business?.name,
    currentStep,
    isLoadingSettings,
    categorySelectionEnabled
  });

  const selectedCategoryData = categories.find(category => category.id === selectedCategory);
  const allServices = categories.flatMap(category => category.services || []);
  const servicesToShow = categorySelectionEnabled
    ? (selectedCategoryData?.services || [])
    : allServices;
  const servicePrice = Number(selectedService?.priceOption?.price || 0);
  const secondaryServicesPrice = selectedSecondaryServices.reduce((sum, sec) => 
    sum + Number(sec.priceOption?.price || 0), 0
  );
  const totalServicePrice = servicePrice + secondaryServicesPrice;
  const taxRateTotal = taxRates.reduce((sum, rate) => sum + (Number(rate.rate) || 0), 0);
  const taxAmount = Math.round((totalServicePrice * taxRateTotal / 100) * 100) / 100;
  const grandTotal = Math.round((totalServicePrice + taxAmount) * 100) / 100;
  const taxLabel = taxRates.length > 0
    ? taxRates.map(rate => `${rate.name} (${rate.rate}%)`).join(' + ')
    : 'Tax';

  const handleSubmitQueue = async () => {
    if (!business?.id || !selectedService) return;
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const existingCustomer = await findExistingCustomer(
        business.id,
        email,
        phone
      );

      let customerId = existingCustomer?.id;

      if (existingCustomer) {
        await updateCustomerFromBooking(existingCustomer.id, {
          first_name: firstName,
          last_name: lastName,
          email,
          phone
        });
      } else {
        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .insert({
            business_id: business.id,
            first_name: firstName,
            last_name: lastName,
            email,
            phone
          })
          .select('id')
          .single();

        if (customerError) throw customerError;
        customerId = customerData?.id;
      }

      if (!customerId) throw new Error('Unable to create customer record');

      const { data: vehicleData, error: vehicleError } = await supabase
        .from('customer_vehicles')
        .insert({
          customer_id: customerId,
          make: vehicleMake,
          model: vehicleModel,
          year: vehicleYear,
          color: vehicleColor
        })
        .select('id')
        .single();

      if (vehicleError) throw vehicleError;
      if (!vehicleData?.id) throw new Error('Unable to create vehicle record');

      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          business_id: business.id,
          customer_id: customerId,
          status: 'scheduled',
          notes: notes || 'Created via queue intake',
          subtotal: totalServicePrice,
          tax_amount: taxAmount,
          total_amount: grandTotal,
          created_by: 'queue',
          in_que: !stripePayEnabled,
          que_step_id: null,
          is_deleted: stripePayEnabled // Mark as deleted if payment required
        })
        .select('id')
        .single();

      if (bookingError) throw bookingError;
      if (!bookingData?.id) throw new Error('Unable to create booking');

      // Insert primary service
      const serviceTaxRate = selectedService.service?.tax_exempt ? 0 : taxRateTotal;
      const serviceTaxAmount = selectedService.service?.tax_exempt ? 0 : (servicePrice * taxRateTotal / 100);
      const serviceTotal = selectedService.service?.tax_exempt ? servicePrice : (servicePrice + serviceTaxAmount);

      const { error: serviceError } = await supabase
        .from('booking_services')
        .insert({
          business_id: business.id,
          booking_id: bookingData.id,
          service_id: selectedService.service?.id,
          vehicle_id: vehicleData.id,
          name: selectedService.service?.name || 'Service',
          description: selectedService.service?.description || '',
          price: servicePrice,
          tax_exempt: selectedService.service?.tax_exempt || false,
          tax_rate: serviceTaxRate,
          tax_amount: serviceTaxAmount,
          total_amount: serviceTotal,
          duration_minutes: selectedService.priceOption?.duration_minutes || 0,
          notes: notes || null
        });

      if (serviceError) throw serviceError;

      // Insert secondary services
      if (selectedSecondaryServices.length > 0) {
        const secondaryServicesToInsert = selectedSecondaryServices.map(secondary => {
          const secPrice = Number(secondary.priceOption?.price || 0);
          const secTaxRate = secondary.service?.tax_exempt ? 0 : taxRateTotal;
          const secTaxAmount = secondary.service?.tax_exempt ? 0 : (secPrice * taxRateTotal / 100);
          const secTotal = secondary.service?.tax_exempt ? secPrice : (secPrice + secTaxAmount);

          return {
            business_id: business.id,
            booking_id: bookingData.id,
            service_id: secondary.service?.id,
            vehicle_id: vehicleData.id,
            name: secondary.service?.name || 'Service',
            description: secondary.service?.description || '',
            price: secPrice,
            tax_exempt: secondary.service?.tax_exempt || false,
            tax_rate: secTaxRate,
            tax_amount: secTaxAmount,
            total_amount: secTotal,
            duration_minutes: secondary.priceOption?.duration_minutes || 0,
            notes: null
          };
        });

        const { error: secondaryError } = await supabase
          .from('booking_services')
          .insert(secondaryServicesToInsert);

        if (secondaryError) throw secondaryError;
      }

      let queuePosition: number | null = null;
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const { data: queueData } = await supabase
          .from('queue_entries')
          .select('queue_position')
          .eq('booking_id', bookingData.id)
          .maybeSingle();

        if (queueData?.queue_position !== undefined && queueData?.queue_position !== null) {
          queuePosition = queueData.queue_position;
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 300));
      }

      let liveLinkToken: string | null = null;
      try {
        const { data: tokenData, error: tokenError } = await supabase.rpc('create_queue_public_link', {
          p_booking_id: bookingData.id
        });

        if (tokenError) {
          console.error('Error creating queue status link:', tokenError);
        } else {
          liveLinkToken = tokenData as string;
        }
      } catch (err) {
        console.error('Error creating queue status link:', err);
      }

      if (phone && liveLinkToken) {
        const liveUrl = `${getSiteUrl()}/${business.slug}/que/status/${liveLinkToken}`;
        await sendSMS({
          businessId: business.id,
          to: phone,
          message: `You're in the queue! Track your live status here: ${liveUrl}`
        });
      }

      if (stripePayEnabled) {
        const { data: paymentData, error: paymentError } = await supabase.functions.invoke('create-queue-payment', {
          body: { bookingId: bookingData.id }
        });

        if (paymentError || !paymentData?.paymentId) {
          throw new Error(paymentError?.message || 'Failed to create payment');
        }

        const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
          body: { paymentId: paymentData.paymentId }
        });

        if (linkError || !linkData?.paymentUrl) {
          throw new Error(linkError?.message || 'Failed to generate payment link');
        }

        window.location.href = linkData.paymentUrl;
        return;
      }

      const positionParam = queuePosition !== null ? `&position=${queuePosition}` : '';
      const tokenParam = liveLinkToken ? `&token=${liveLinkToken}` : '';
      window.location.href = `${getSiteUrl()}/${business.slug}/que/thank-you?booking=${bookingData.id}${positionParam}${tokenParam}`;
    } catch (err) {
      console.error('Error creating queue booking:', err);
      setSubmissionError('Unable to submit your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {headerEnabled && (
        <div
          className="h-24 w-full flex items-center justify-center px-6 sm:px-8 relative z-10"
          style={{
            backgroundColor: headerColor,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div className="flex items-center justify-center gap-4">
            {displayLogo && business?.booking_settings?.logo_url && (
              <img
                src={business.booking_settings.logo_url}
                alt="Business logo"
                className="h-20 sm:h-[84px] md:h-24 max-w-[360px] sm:max-w-[440px] object-contain"
                style={{
                  maxHeight: '96px',
                  filter: business.booking_settings.logo_invert
                    ? 'brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                    : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            {displayCompanyName && (
              <div className="text-white text-lg sm:text-xl font-semibold">
                {business?.name || 'Company'}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-2xl px-4 py-10 mx-auto">
        {isLoadingSettings ? (
          <div className="text-center text-lg font-medium text-gray-600">
            Loading queue settings...
          </div>
        ) : currentStep === 'category' ? (
          <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{categorySelectionLabel}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              {isCategoriesLoading ? (
                <div className="text-center col-span-full text-lg font-medium text-gray-600">
                  Loading categories...
                </div>
              ) : error ? (
                <div className="text-center col-span-full">
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                    <div className="text-red-600 text-base font-medium">{error}</div>
                  </div>
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center col-span-full text-lg font-medium text-gray-600">
                  No queue categories available
                </div>
              ) : (
                categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setCurrentStep('service');
                    }}
                    className={`p-5 sm:p-6 rounded-2xl border-2 transition-all duration-200 active:scale-95 text-base sm:text-lg font-semibold ${
                      selectedCategory === category.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                    }`}
                    style={{
                      boxShadow: selectedCategory === category.id
                        ? '0 4px 12px rgba(59, 130, 246, 0.15), 0 2px 4px rgba(59, 130, 246, 0.1)'
                        : '0 2px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)'
                    }}
                  >
                    <h3 className="font-bold">{category.category_title}</h3>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : currentStep === 'service' ? (
          <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Select Service</h2>
            {isCategoriesLoading ? (
              <div className="text-center text-lg font-medium text-gray-600">Loading services...</div>
            ) : error ? (
              <div className="text-center">
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                  <div className="text-red-600 text-base font-medium">{error}</div>
                </div>
              </div>
            ) : servicesToShow.length === 0 ? (
              <div className="text-center text-lg font-medium text-gray-600">No services available</div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                {servicesToShow.map((item, index) => (
                  <div
                    key={`${item.service?.id}-${item.priceOption?.id || index}`}
                    className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 space-y-4 relative"
                    style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)' }}
                  >
                    <div>
                      <h4 className="text-xl sm:text-2xl font-bold text-gray-900">
                        {item.service?.name || 'Service'}
                      </h4>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      {item.priceOption?.price !== undefined && (
                        <p className="text-gray-900 text-base sm:text-lg font-semibold">
                          ${Number(item.priceOption.price).toLocaleString()}
                        </p>
                      )}
                    </div>

                    <div className="overflow-hidden transition-all duration-300 ease-in-out">
                      {expandedServiceId === item.service?.id && (
                        <div className="pt-3 pb-1 animate-in slide-in-from-top-2 duration-300">
                          {item.service?.description ? (
                            <RichTextDescription
                              description={item.service.description}
                              className="text-sm font-medium text-gray-800 leading-relaxed"
                            />
                          ) : (
                            <p className="text-sm font-medium text-gray-600">No description available.</p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedServiceId(
                            expandedServiceId === item.service?.id ? null : item.service?.id || null
                          );
                        }}
                        className="w-full text-sm font-medium text-gray-600 hover:text-gray-900 transition-all duration-200 active:scale-95 py-2.5 px-4 rounded-2xl border border-gray-200 hover:border-gray-300"
                      >
                        {expandedServiceId === item.service?.id ? 'Hide Description' : 'Show Description'}
                      </button>

                      <button
                        onClick={() => {
                          // Ensure we preserve secondary_services when selecting a service
                          // The item should already have secondary_services from the category data
                          console.log('🔵 CLICKED SERVICE BUTTON - Full item:', JSON.stringify(item, null, 2));
                          console.log('🔵 Item has secondary_services?', !!item.secondary_services);
                          console.log('🔵 Secondary services array:', item.secondary_services);
                          setSelectedService(item);
                          setSelectedSecondaryServices([]); // Reset secondary services when selecting a new service
                          
                          // If this service has secondary services, go to secondary-services step
                          // Otherwise go directly to intake
                          if (item.secondary_services && item.secondary_services.length > 0) {
                            setCurrentStep('secondary-services');
                          } else {
                            setCurrentStep('intake');
                          }
                        }}
                        className={`w-full py-3 px-6 text-center font-semibold rounded-2xl transition-all duration-200 active:scale-95 ${
                          selectedService?.service?.id === item.service?.id &&
                          selectedService?.priceOption?.id === item.priceOption?.id
                            ? 'bg-blue-500 text-white'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                        style={{
                          boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25), 0 1px 3px rgba(59, 130, 246, 0.15)'
                        }}
                      >
                        SELECT
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        ) : currentStep === 'secondary-services' ? (
          <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Additional Services</h2>
                <p className="text-sm text-gray-600">
                  Select any add-on services you'd like to include with <span className="font-semibold">{selectedService?.service?.name}</span> (Optional)
                </p>
              </div>
            </div>
            
            {/* Continue/Skip buttons - At the Top */}
            <div className="mb-6 flex justify-end items-center gap-4 pb-4 border-b border-gray-200">
              <button
                onClick={() => {
                  setSelectedSecondaryServices([]);
                  setCurrentStep('intake');
                }}
                className="py-2.5 px-6 text-gray-600 hover:text-gray-900 font-semibold rounded-2xl transition-all duration-200 active:scale-95 border-2 border-gray-300 hover:border-gray-400"
              >
                Skip
              </button>
              <button
                onClick={() => setCurrentStep('intake')}
                className="py-2.5 px-8 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-bold rounded-2xl transition-all duration-200 active:scale-95"
                style={{
                  boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25), 0 1px 3px rgba(59, 130, 246, 0.15)'
                }}
              >
                Continue {selectedSecondaryServices.length > 0 && `(${selectedSecondaryServices.length} selected)`}
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-6">
              {selectedService?.secondary_services
                ?.filter((secondary: any) => {
                  // Secondary services must have both service and priceOption (just like regular services)
                  const service = secondary.service || secondary;
                  const priceOption = secondary.priceOption || secondary;
                  return secondary && service && service.id && priceOption && (priceOption.id || priceOption.price_option_id);
                })
                .map((secondary: any, index: number) => {
                  console.log('Rendering secondary service:', secondary);
                  // Secondary services have the same structure as regular services: {service, priceOption}
                  const service = secondary.service;
                  const priceOption = secondary.priceOption;
                  
                  if (!service || !service.id || !priceOption) {
                    console.warn('Invalid secondary service structure - must have both service and priceOption:', secondary);
                    return null;
                  }
                  
                  // Match by both service ID and price option ID (just like regular services)
                  const isSelected = selectedSecondaryServices.some(
                    s => {
                      const sService = s.service || s;
                      const sPriceOption = s.priceOption || s;
                      const secService = secondary.service || secondary;
                      const secPriceOption = secondary.priceOption || secondary;
                      // Must match both service ID and price option ID
                      return sService?.id === secService?.id &&
                        (sPriceOption?.id || sPriceOption?.price_option_id) === 
                        (secPriceOption?.id || secPriceOption?.price_option_id);
                    }
                  );
                  
                  return (
                    <div
                      key={`${service.id}-${priceOption?.id || priceOption?.price_option_id || index}`}
                      onClick={() => {
                        if (isSelected) {
                          // Remove by matching both service ID and price option ID
                          setSelectedSecondaryServices(
                            selectedSecondaryServices.filter(
                              s => {
                                const sService = s.service || s;
                                const sPriceOption = s.priceOption || s;
                                const secService = secondary.service || secondary;
                                const secPriceOption = secondary.priceOption || secondary;
                                // Must match both service ID and price option ID to remove
                                return !(sService?.id === secService?.id &&
                                  (sPriceOption?.id || sPriceOption?.price_option_id) === 
                                  (secPriceOption?.id || secPriceOption?.price_option_id));
                              }
                            )
                          );
                        } else {
                          // Add secondary service with both service and priceOption (just like regular services)
                          setSelectedSecondaryServices([...selectedSecondaryServices, {
                            service: secondary.service || secondary,
                            priceOption: secondary.priceOption || secondary
                          }]);
                        }
                      }}
                      className="bg-white rounded-2xl border-2 p-6 sm:p-8 space-y-4 relative cursor-pointer transition-all duration-200 hover:shadow-lg"
                      style={{
                        borderColor: isSelected ? '#3b82f6' : '#e5e7eb',
                        backgroundColor: isSelected ? '#eff6ff' : 'white',
                        boxShadow: isSelected 
                          ? '0 4px 12px rgba(59, 130, 246, 0.15), 0 2px 4px rgba(59, 130, 246, 0.1)'
                          : '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)'
                      }}
                    >
                      {/* Checkmark in top right when selected */}
                      {isSelected && (
                        <div className="absolute top-4 right-4 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      
                      <div>
                        <h4 className="text-xl sm:text-2xl font-bold text-gray-900">
                          {service?.name || 'Service'}
                        </h4>
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        {priceOption?.price !== undefined && (
                          <p className="text-gray-900 text-base sm:text-lg font-semibold">
                            ${Number(priceOption.price).toLocaleString()}
                          </p>
                        )}
                        {priceOption?.name && (
                          <span className="text-sm text-gray-600">
                            {priceOption.name}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : (
          <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
            <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Enter Your Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10">
                <div className="md:order-2">
                  <div className="space-y-5 sm:space-y-6">
                    <div className="grid grid-cols-2 gap-4 sm:gap-5">
                      <div>
                        <label htmlFor="firstName" className="block text-base font-semibold text-gray-700 mb-2">
                          First Name*
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                          placeholder="Your first name"
                        />
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-base font-semibold text-gray-700 mb-2">
                          Last Name*
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                          placeholder="Your last name"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-base font-semibold text-gray-700 mb-2">
                        Phone Number*
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                        placeholder="(555) 555-5555"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-base font-semibold text-gray-700 mb-2">
                        Email Address*
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                        placeholder="your@email.com"
                      />
                    </div>

                    {/* Vehicle Year, Make, Model */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
                      <div>
                        <label htmlFor="vehicleYear" className="block text-base font-semibold text-gray-700 mb-2">
                          Vehicle Year*
                        </label>
                        <input
                          id="vehicleYear"
                          type="text"
                          value={vehicleYear}
                          onChange={(e) => setVehicleYear(e.target.value)}
                          className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                          placeholder="2024"
                        />
                      </div>

                      <div>
                        <label htmlFor="vehicleMake" className="block text-base font-semibold text-gray-700 mb-2">
                          Vehicle Make*
                        </label>
                        <input
                          id="vehicleMake"
                          type="text"
                          value={vehicleMake}
                          onChange={(e) => setVehicleMake(e.target.value)}
                          className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                          placeholder="Toyota"
                        />
                      </div>

                      <div>
                        <label htmlFor="vehicleModel" className="block text-base font-semibold text-gray-700 mb-2">
                          Vehicle Model*
                        </label>
                        <input
                          id="vehicleModel"
                          type="text"
                          value={vehicleModel}
                          onChange={(e) => setVehicleModel(e.target.value)}
                          className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                          placeholder="Camry"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="vehicleColor" className="block text-base font-semibold text-gray-700 mb-2">
                        Vehicle Color*
                      </label>
                      <input
                        id="vehicleColor"
                        type="text"
                        value={vehicleColor}
                        onChange={(e) => setVehicleColor(e.target.value)}
                        className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                        placeholder="Black"
                        required
                      />
                    </div>

                    {/* Notes */}
                    <div>
                      <label htmlFor="notes" className="block text-base font-semibold text-gray-700 mb-2">
                        Additional Notes <span className="text-gray-500 font-normal">(optional)</span>
                      </label>
                      <textarea
                        id="notes"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="block w-full rounded-lg border-2 border-gray-300 shadow-sm py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                        placeholder="Any special instructions for your service provider"
                      />
                    </div>

                    <div className="pt-4 sm:pt-6">
                      <button
                        type="button"
                        onClick={handleSubmitQueue}
                        disabled={
                          isSubmitting ||
                          !firstName ||
                          !lastName ||
                          !vehicleYear ||
                          !vehicleMake ||
                          !vehicleModel ||
                          !vehicleColor ||
                          !email ||
                          !phone
                        }
                        className="w-full flex justify-center py-4 px-6 border border-transparent rounded-xl shadow-lg text-xl font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
                      >
                        {isSubmitting ? 'Submitting...' : stripePayEnabled ? 'Pay Now' : 'Submit'}
                      </button>
                      {submissionError && (
                        <p className="mt-3 text-base text-red-600 font-medium">{submissionError}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg md:order-1 mt-5 md:mt-0">
                  <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Service Summary</h3>

                  {selectedService ? (
                    <div className="space-y-3 sm:space-y-4">
                      <div className="pb-3 border-b border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Services Selected:</h4>
                        
                        {/* Main Service */}
                        <div className="bg-blue-50 rounded-lg p-3 mb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">
                                {selectedService.service?.name || 'Service'}
                              </div>
                              {selectedService.priceOption?.name && (
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {selectedService.priceOption.name}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900">
                                ${Number(selectedService.priceOption?.price || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Secondary Services */}
                        {selectedSecondaryServices.length > 0 && (
                          <>
                            <div className="text-xs font-semibold text-gray-600 mt-3 mb-2">Add-ons:</div>
                            {selectedSecondaryServices.map((secondary, idx) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-3 mb-2">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                      {secondary.service?.name || 'Service'}
                                    </div>
                                    {secondary.priceOption?.name && (
                                      <div className="text-xs text-gray-600 mt-0.5">
                                        {secondary.priceOption.name}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm font-medium text-gray-900">
                                      ${Number(secondary.priceOption?.price || 0).toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>

                      <div className="pt-2 sm:pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-xs sm:text-sm">Subtotal</span>
                          <span className="font-medium text-sm sm:text-base">
                            ${totalServicePrice.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1 sm:mt-2">
                          <span className="text-gray-600 text-xs sm:text-sm">
                            {taxLabel}
                          </span>
                          <span className="font-medium text-sm sm:text-base">
                            {isTaxLoading ? '...' : `$${taxAmount.toFixed(2)}`}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1 sm:mt-2 pt-1 sm:pt-2 border-t">
                          <span className="font-semibold text-sm sm:text-lg">Total</span>
                          <span className="font-semibold text-sm sm:text-lg">
                            {isTaxLoading ? '...' : `$${grandTotal.toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">No service selected.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

